import React, { useEffect, useCallback } from 'react';
import get from 'lodash/get';
// @ts-expect-error - TS2614 - Module '"history/createBrowserHistory"' has no exported member 'BrowserHistory'. Did you mean to use 'import BrowserHistory from "history/createBrowserHistory"' instead?
import type { BrowserHistory } from 'history/createBrowserHistory';
import queryString from 'query-string';
import { SPA_SSR_RENDERED_MARK } from '@jira/platform__common-constants/src/analytics-marks.js';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import clearMarks from '@atlassian/jira-common-performance/src/clear-marks-with-prefix.tsx';

import { getTenantContext_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-common-util-get-tenant-context/src/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { isFetchError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { RouteContext, Query } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import {
	LOG_LOCATION,
	SEARCH_FILTER,
	SHOW_PROJECT_BUILDER_FILTER,
	PRESERVED_ANONYMOUS_REDIRECT_PARAMS,
} from './LastVisitedProjectPageLogLocation.constants.tsx';
import { getNow } from './getNow.tsx';

// Injectable wrapper for window.location.replace to enable testing
export const windowLocationReplace = (url: string): void => {
	if (typeof window !== 'undefined') {
		window.location.replace(url);
	}
};

const buildEndpoint = (projectKey: string, initialRedirect: boolean) =>
	`/rest/internal/2/projectlandingpage/${projectKey}${initialRedirect ? '?initialRedirect=true' : ''}`;
type Props = RouteContext & {
	history: BrowserHistory;
};
type SearchParams = {
	[key: string]: string;
};
const allowListedSearchParams = [SEARCH_FILTER, SHOW_PROJECT_BUILDER_FILTER];
const redirectForAnonymousUser = (
	projectKey: string,
	startTime: number,
	locationReplace: (url: string) => void,
	query: Query,
) => {
	const segment = fg('jira_list_nin_merge') ? 'list' : 'issues';
	const preservedParams = new URLSearchParams();
	PRESERVED_ANONYMOUS_REDIRECT_PARAMS.forEach((key) => {
		if (query[key]) preservedParams.set(key, String(query[key]));
	});
	preservedParams.set('filter', 'allissues');
	const destination = `/projects/${projectKey}/${segment}/?${preservedParams.toString()}`;
	locationReplace(destination);

	// Log success
	log.safeInfoWithoutCustomerData(
		LOG_LOCATION,
		'History replace correctly triggered for anonymous user',
		{
			requestTime: getNow() - startTime,
			destination,
		},
	);
};
export const getFilteredSearchParams = (query: Query): SearchParams =>
	Object.fromEntries(
		Object.entries(query).filter(
			([key]) =>
				allowListedSearchParams.some((allowedParam) => key === allowedParam) ||
				key.includes(SEARCH_FILTER),
		),
	);
export const getURLSearchQueryParams = (
	searchQuery: SearchParams,
	queryInitializer: string,
): string => {
	const queryKeys = new URLSearchParams(searchQuery).toString();
	return queryKeys.length > 0 ? `${queryInitializer}${queryKeys}` : '';
};

/**
 * Handles the redirection of users to the most recently accessed project page (functional component version).
 * This function checks for the project key in the URL or route parameters, fetches the required project data,
 * and redirects the user accordingly. It also manages error logging for failed redirections and provides
 * a tailored experience for anonymous users.
 */
const LastVisitedProjectPageRedirect = (props: Props): React.JSX.Element => {
	const {
		history,
		location,
		query,
		match: { params },
	} = props;

	const performRedirect = useCallback(() => {
		// First time a project is created
		const { initial } = query;

		// Clear the SSR Ready Mark if present
		clearMarks(SPA_SSR_RENDERED_MARK);
		const startTime = getNow();
		const projectKey =
			// @ts-expect-error - TS2345 - Argument of type 'string | {}' is not assignable to parameter of type 'string'.
			params.projectKey || queryString.parse(get(location, 'search', {})).projectKey;
		if (!params.projectKey) {
			log.safeInfoWithoutCustomerData(LOG_LOCATION, 'queryString', {
				duration: getNow() - startTime,
			});
		}
		if (projectKey) {
			// Ensure project keys are uppercase
			const normalizedProjectKey = projectKey.toUpperCase();
			fetchJson(buildEndpoint(normalizedProjectKey, !!initial)).then(
				(response) => {
					const endTime = getNow();
					let searchQueryParams = '';
					const searchQuery = getFilteredSearchParams(query);
					const queryInitializer = response.data.url.includes('?') ? '&' : '?';
					searchQueryParams = getURLSearchQueryParams(searchQuery, queryInitializer);
					const destination = response.data.url + searchQueryParams;
					history.replace(destination);
					// Log success
					log.safeInfoWithoutCustomerData(LOG_LOCATION, 'History replace correctly triggered', {
						requestTime: endTime - startTime,
						destination,
					});
				},
				(error: Error) => {
					const endTime = getNow();
					const is404 = isFetchError(error) && error.statusCode === 404;

					if (getTenantContext_DEPRECATED_DO_NOT_USE().isAnonymous) {
						redirectForAnonymousUser(normalizedProjectKey, startTime, windowLocationReplace, query);
					} else if (is404) {
						// Redirect back to the home page if project not found
						history.replace('/jira/for-you');
					} else {
						// Log failures
						log.safeErrorWithoutCustomerData(
							LOG_LOCATION,
							`Error on request. Request time: ${endTime - startTime}`,
							error,
						);
					}
				},
			);
		}
	}, [history, location, query, params]);

	useEffect(() => {
		performRedirect();
	}, [performRedirect]);

	return <UFOLoadHold name="last-visited-project-page-redirect" />;
};

export default LastVisitedProjectPageRedirect;
